<script setup>
import { computed, ref, reactive, watch, onMounted } from 'vue';

const props = defineProps({
    fillColors: {
        type: Array,
        default: () => ['#7814d7'],
    },
    pointColors: {
        type: Array,
        default: () => [],
    },
    labels: {
        type: Array,
        required: false,
        default: () => ['Label 1', 'Label 2', 'Label 3', 'Label 4'],
    },
    lazyRender: {
        type: Boolean,
        default: false,
    },
    series: {
        type: Array,
        required: false,
        default: () => [
            {
                name: 'Series 1',
                data: [50, 40, 35, 20],
            },
        ],
    },
    showDataLabels: {
        type: Boolean,
        default: false,
    },
    dataLabelPrefix: {
        type: String,
        default: '',
    },
    dataLabelSuffix: {
        type: String,
        default: '',
    },
    showLegend: {
        type: Boolean,
        default: false,
    },
    showTooltip: {
        type: Boolean,
        default: false,
    },
    strokeCurve: {
        type: String,
        default: 'smooth',
    },
});

const formatValue = (val) => {
    if (val === null || val === undefined) {
        return '';
    }

    const num = Number(val);

    if (Number.isNaN(num)) {
        return `${props.dataLabelPrefix}${val}${props.dataLabelSuffix}`;
    }

    return `${props.dataLabelPrefix}${num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}${props.dataLabelSuffix}`;
};

const truncateLabel = (label) => {
    if (label === null || label === undefined) {
        return '';
    }

    const stringLabel = String(label);

    if (stringLabel.length <= 10) {
        return stringLabel;
    }

    return `${stringLabel.slice(0, 10)}…`;
};

const cseries = ref([]);

const discreteMarkers = (colors) => {
    if (!Array.isArray(colors) || colors.length === 0) {
        return [];
    }

    return colors.map((color, index) => ({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: color,
        strokeColor: '#ffffff',
        size: 4,
    }));
};

const chartOptions = reactive({
    chart: {
        animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600,
            animateGradually: {
                enabled: true,
                delay: 120,
            },
            dynamicAnimation: {
                enabled: true,
                speed: 450,
            },
        },
        toolbar: {
            show: false,
        },
    },
    colors: props.fillColors,
    stroke: {
        curve: props.strokeCurve,
        width: 3,
    },
    markers: {
        size: 4,
        colors: props.fillColors,
        strokeColors: '#ffffff',
        strokeWidth: 2,
        discrete: discreteMarkers(props.pointColors),
        hover: {
            sizeOffset: 2,
        },
    },
    legend: {
        show: props.showLegend,
        labels: {
            colors: props.fillColors,
        },
        markers: {
            width: 10,
            height: 10,
            fillColors: props.fillColors,
        },
    },
    tooltip: {
        enabled: props.showTooltip,
        x: {
            formatter(val, opts) {
                const labelFromProps = props.labels?.[opts.dataPointIndex];
                const labelFromChart = opts?.w?.globals?.categoryLabels?.[opts.dataPointIndex];
                return labelFromProps ?? labelFromChart ?? val;
            },
        },
        y: {
            formatter(val) {
                return formatValue(val);
            },
        },
    },
    xaxis: {
        categories: props.labels,
        labels: {
            formatter(val) {
                return truncateLabel(val);
            },
        },
    },
    dataLabels: {
        enabled: props.showDataLabels,
        formatter(val) {
            return formatValue(val);
        },
    },
    yaxis: {
        labels: {
            formatter(val) {
                return formatValue(val);
            },
        },
    },
});

const target = ref(null);
const isVisible = ref(false);
const chartKey = computed(() => {
    const labelKey = Array.isArray(props.labels) ? props.labels.join('|') : '';
    const seriesKey = Array.isArray(props.series)
        ? props.series.map((dataset) => dataset?.data?.length || 0).join(',')
        : '';
    const colorKey = Array.isArray(props.fillColors) ? props.fillColors.join('|') : '';
    const pointColorKey = Array.isArray(props.pointColors) ? props.pointColors.join('|') : '';

    return `${labelKey}-${props.series.length}-${seriesKey}-${colorKey}-${pointColorKey}`;
});

const observer = new IntersectionObserver(
    ([entry]) => {
        isVisible.value = entry.isIntersecting;
    },
    {
        threshold: 0.5,
    }
);

watch(
    () => isVisible.value,
    (newVal) => {
        if (props.lazyRender && newVal) {
            cseries.value = props.series;
        }
    }
);

watch(
    [() => props.series, () => props.labels, () => props.fillColors, () => props.pointColors, () => props.showLegend],
    ([nextSeries, nextLabels, nextColors, nextPointColors, nextShowLegend]) => {
        chartOptions.xaxis.categories = nextLabels;
        chartOptions.colors = nextColors;
        chartOptions.markers.colors = nextColors;
        chartOptions.markers.discrete = discreteMarkers(nextPointColors);
        chartOptions.legend.show = nextShowLegend;
        chartOptions.legend.labels.colors = nextColors;
        chartOptions.legend.markers.fillColors = nextColors;

        if (props.lazyRender && !isVisible.value) {
            cseries.value = nextSeries.map((dataset) => ({
                ...dataset,
                data: dataset.data.map(() => 0),
            }));
            return;
        }

        cseries.value = nextSeries;
    },
    { deep: true }
);

onMounted(() => {
    if (props.lazyRender) {
        cseries.value = props.series.map((dataset) => ({
            ...dataset,
            data: dataset.data.map(() => 0),
        }));
    } else {
        cseries.value = props.series;
    }

    if (target.value?.$el) {
        observer.observe(target.value.$el);
    }
});
</script>

<template>
    <apexchart
        ref="target"
        :key="chartKey"
        type="line"
        height="350"
        :options="chartOptions"
        :series="cseries"
    ></apexchart>
</template>
